import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/lib/LocaleContext";
import { localisedTo } from "@/lib/i18nRoutes";
import { api } from "@/lib/api";
import CollectionCard from "@/components/CollectionCard";
import { AlbumCollectionCard, useAlbums } from "@/pages/Albums";

/**
 * Home-page discovery block: two navigation cards → /music/premium and /music/free.
 *
 * ZERO data fetches for the two persistent cards.  The Edits & Funky
 * card is gated by a cheap single-row availability probe:
 *
 *   GET /api/tracks/edits/availability -> { "available": bool }
 *
 * The card is entirely hidden until the probe confirms at least one
 * published edit exists.  We NEVER preload the edits catalogue,
 * covers, waveforms, or audio here — only a `{available}` boolean.
 */
export default function HomeCollections() {
  const { t } = useTranslation();
  const { lang } = useLocale();
  const [editsAvailable, setEditsAvailable] = useState(false);
  const albums = useAlbums();
  const cardCount = 2 + (editsAvailable ? 1 : 0) + albums.length;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/site/edits-availability");
        if (alive) setEditsAvailable(Boolean(res.data?.available));
      } catch {
        if (alive) setEditsAvailable(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <section
      id="collections"
      data-testid="home-collections"
      className="bg-black text-white pt-0 pb-16 sm:pb-20 px-6 md:px-10"
    >
      <div className="max-w-[1400px] mx-auto">
        <div
          className={`grid grid-cols-1 gap-5 sm:gap-6 ${
            cardCount === 3 ? "md:grid-cols-3" : "md:grid-cols-2"
          }`}
        >
          <CollectionCard
            variant="premium"
            to={localisedTo("/music/premium", lang)}
            kicker={t("music.landing.premiumKicker")}
            title={t("music.landing.premiumTitle")}
            lead={t("music.landing.premiumLead")}
            cta={t("music.landing.premiumCta")}
            testid="home-collections-premium"
          />
          <CollectionCard
            variant="free"
            to={localisedTo("/music/free", lang)}
            kicker={t("music.landing.freeKicker")}
            title={t("music.landing.freeTitle")}
            lead={t("music.landing.freeLead")}
            cta={t("music.landing.freeCta")}
            testid="home-collections-free"
          />
          {editsAvailable && (
            <CollectionCard
              variant="edits"
              to={localisedTo("/music/edits", lang)}
              kicker={t("music.landing.editsKicker")}
              title={t("music.landing.editsTitle")}
              lead={t("music.landing.editsLead")}
              cta={t("music.landing.editsCta")}
              testid="home-collections-edits"
            />
          )}
          {albums.map((a) => <AlbumCollectionCard key={a.id} album={a} />)}
        </div>
      </div>
    </section>
  );
}
